/**
 * Production data boundary for the medical-records export builder.
 *
 * Adapts the generated backend contract (`@store/generated/medical-record-export`
 * via the feature store wrapper) into the dialog's view models, and implements
 * the generate → poll → download lifecycle against the async export job.
 */
import { useCallback, useMemo } from 'react';
import { useAppDispatch } from '@store/hooks';

import type {
  MedicalRecordExportOptionsResponse,
  MedicalRecordExportStatusResponse,
  MedicalRecordFailureCode,
} from '../../store/api';
import {
  medicalRecordExportApi,
  useGetMedicalRecordExportOptionsQuery,
  useStartMedicalRecordExportMutation,
} from '../../store/api';

import type {
  GeneratedMedicalRecordPacket,
  MedicalRecordDocumentOption,
  MedicalRecordEncounterOption,
  MedicalRecordsExportSelection,
} from './types';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Shape of a dispatched RTK Query `initiate` subscription. The store's
 * dispatch type erases the thunk overload for dynamically injected
 * endpoints, so the result is asserted to the documented RTK contract.
 */
interface QuerySubscription<T> {
  unwrap: () => Promise<T>;
  unsubscribe: () => void;
}

const formatSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};

const formatExpiry = (iso?: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

/**
 * Fields added to the status contract by the backend failed-record work.
 * Kept as a local extension until the generated types pick them up from the
 * deployed OpenAPI spec; the intersection stays valid after regeneration.
 */
interface FailureAttribution {
  failed_record_id?: string | null;
  failed_record_kind?: string | null;
}

/**
 * Actionable copy for structured job failure codes. When the failing record
 * is known its title is named so the clinician doesn't have to guess which
 * selection is affected (dialog-only copy — titles never leave the client).
 */
export const failureMessage = (
  code?: MedicalRecordFailureCode | (string & {}) | null,
  recordTitle?: string,
): string => {
  const record = recordTitle ? `“${recordTitle}”` : undefined;
  switch (code) {
    case 'page_limit_exceeded':
    case 'byte_limit_exceeded':
      return 'This packet is larger than the export limit. Reduce the selected records and try again.';
    case 'selection_stale':
    case 'note_not_eligible':
    case 'record_not_found':
    case 'record_not_accessible':
      return record
        ? `${record} changed while the packet was being built. Review the selection and try again.`
        : 'Some selected records changed while the packet was being built. Review the selection and try again.';
    case 'invalid_document_page_range':
    case 'unsupported_document_type':
    case 'source_unreadable':
      return record
        ? `${record} could not be read. Remove it from the selection and try again.`
        : 'One of the selected documents could not be read. Remove it from the selection and try again.';
    case 'signed_copy_unavailable':
      return record
        ? `The signed copy of ${record} couldn't be retrieved. This is usually temporary — try again in a few minutes.`
        : "The signed copy of a note couldn't be retrieved. This is usually temporary — try again in a few minutes.";
    case 'export_revoked':
      return 'This export was revoked before it finished.';
    default:
      return 'The record packet could not be generated. Try again.';
  }
};

/** Resolve a failed record id to its display title from the loaded options. */
export const findMedicalRecordTitle = (
  encounters: MedicalRecordEncounterOption[],
  documents: MedicalRecordDocumentOption[],
  recordId?: string | null,
): string | undefined => {
  if (!recordId) return undefined;
  for (const encounter of encounters) {
    if (encounter.id === recordId) return encounter.title;
    const note = encounter.notes.find(n => n.id === recordId);
    if (note) return note.title;
  }
  return documents.find(d => d.id === recordId)?.title;
};

const toEncounterOptions = (
  options?: MedicalRecordExportOptionsResponse,
): MedicalRecordEncounterOption[] =>
  (options?.encounters ?? []).map(encounter => ({
    id: encounter.encounter_id,
    title: encounter.title,
    occurredAt: encounter.occurred_at ?? '',
    providerName: encounter.provider_name ?? '',
    notes: (encounter.notes ?? []).map(note => ({
      id: note.note_id,
      title: note.title,
      documentType: note.document_type ?? 'Note',
      pageCount: note.page_estimate,
      pdfSource: note.pdf_source,
      signedAt: note.signed_at ?? undefined,
    })),
  }));

const toDocumentOptions = (
  options?: MedicalRecordExportOptionsResponse,
): MedicalRecordDocumentOption[] =>
  (options?.documents ?? []).map(document => ({
    id: document.document_reference_id,
    title: document.title,
    documentType: document.document_type ?? 'Document',
    authoredAt: document.authored_at ?? '',
    pageCount: document.page_estimate,
    sourcePageRange: document.source_page_range
      ? {
          start: document.source_page_range.start,
          end: document.source_page_range.end,
          total: document.source_page_range.total ?? undefined,
        }
      : undefined,
    eligible: document.eligible !== false,
    ineligibleReason: document.ineligible_reason ?? undefined,
    // Ineligible documents are shown disabled and never preselected.
    selectedByDefault: document.eligible !== false,
  }));

const toPacket = (status: MedicalRecordExportStatusResponse): GeneratedMedicalRecordPacket => ({
  exportId: status.export_id,
  fileName: status.filename ?? 'medical-records.pdf',
  sizeLabel: formatSize(status.output_size_bytes),
  expiresAtLabel: formatExpiry(status.artifact_expires_at),
  outputMode: status.output_mode,
});

interface UseMedicalRecordsExportArgs {
  patientId: string;
  /** Only fetch options while the dialog is open. */
  enabled: boolean;
}

export function useMedicalRecordsExport({ patientId, enabled }: UseMedicalRecordsExportArgs) {
  const dispatch = useAppDispatch();
  const optionsQuery = useGetMedicalRecordExportOptionsQuery(
    { patientId },
    { skip: !enabled || !patientId, refetchOnMountOrArgChange: true },
  );
  const [startExport] = useStartMedicalRecordExportMutation();

  const encounters = useMemo(() => toEncounterOptions(optionsQuery.data), [optionsQuery.data]);
  const documents = useMemo(() => toDocumentOptions(optionsQuery.data), [optionsQuery.data]);

  const fetchStatus = useCallback(
    async (exportId: string): Promise<MedicalRecordExportStatusResponse> => {
      // The app tsconfig types dispatch without the injected-endpoint thunk
      // overload, so assert the documented RTK subscription shape.
      const request = dispatch(
        medicalRecordExportApi.endpoints.getMedicalRecordExportStatus.initiate(
          { exportId },
          { forceRefetch: true },
        ),
      ) as unknown as QuerySubscription<MedicalRecordExportStatusResponse>;
      try {
        return await request.unwrap();
      } finally {
        request.unsubscribe();
      }
    },
    [dispatch],
  );

  const generatePacket = useCallback(
    async (selection: MedicalRecordsExportSelection): Promise<GeneratedMedicalRecordPacket> => {
      let started;
      try {
        started = await startExport({
          patientId,
          startMedicalRecordExportRequest: {
            encounter_ids: selection.encounterIds,
            note_ids: selection.noteIds,
            document_reference_ids: selection.documentReferenceIds,
            output_mode: selection.outputMode,
            ordering: 'chronological',
          },
        }).unwrap();
      } catch (error) {
        const data = (error as { data?: { message?: string; error_code?: string } })?.data;
        throw new Error(
          data?.message || 'The export could not be started. Try again.',
        );
      }

      // No explicit cancellation on dialog close: the dialog disables
      // Generate/Cancel/Back/backdrop-close while generating, so the poll can't
      // outlive an open dialog today. If that invariant ever changes, thread an
      // AbortSignal (or a mounted ref) through here to stop the loop.
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        const status = await fetchStatus(started.export_id);
        if (status.status === 'completed') {
          return toPacket(status);
        }
        if (status.status === 'failed') {
          const { failed_record_id } = status as MedicalRecordExportStatusResponse &
            FailureAttribution;
          throw new Error(
            failureMessage(
              status.failure_code,
              findMedicalRecordTitle(encounters, documents, failed_record_id),
            ),
          );
        }
        await delay(POLL_INTERVAL_MS);
      }
      throw new Error(
        'The export is taking longer than expected. It will keep building — reopen this dialog in a moment.',
      );
    },
    [documents, encounters, fetchStatus, patientId, startExport],
  );

  const downloadPacket = useCallback(
    async (packet: GeneratedMedicalRecordPacket): Promise<void> => {
      if (!packet.exportId) {
        throw new Error('This packet is no longer available.');
      }
      // See fetchStatus: assert the RTK subscription shape.
      const request = dispatch(
        medicalRecordExportApi.endpoints.downloadMedicalRecordExport.initiate(
          { exportId: packet.exportId },
          { forceRefetch: true },
        ),
      ) as unknown as QuerySubscription<{ download_url: string; filename: string }>;
      let download;
      try {
        download = await request.unwrap();
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 410) {
          throw new Error(
            'This packet has expired or was revoked. Generate a new one to download it.',
          );
        }
        throw new Error('The download link could not be created. Try again.');
      }
      // The presigned URL forces an attachment download via its
      // ResponseContentDisposition; a plain navigation saves the file.
      const anchor = document.createElement('a');
      anchor.href = download.download_url;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
    [dispatch],
  );

  return {
    encounters,
    documents,
    excludedNoteCount: optionsQuery.data?.excluded_note_count ?? 0,
    isLoadingOptions: optionsQuery.isLoading || optionsQuery.isFetching,
    optionsError: optionsQuery.isError,
    refetchOptions: optionsQuery.refetch,
    generatePacket,
    downloadPacket,
  };
}
